import { create } from 'zustand'
import type { TracksData } from './tracks.store.tsx'
import useTracksStore, { TracksStoreInitialState } from './tracks.store.tsx'
import { isDeepEqual } from '@/utils/is-deep-equal.ts'

export type SaveStatus =
  | 'persisted' // Changes on server have been saved
  | 'dirty'
  | 'syncing'
  | 'failed'
  | 'broken'

interface HistoryState {
  history: TracksData[]
  future: TracksData[]
  currentState: TracksData
  saveStatus: SaveStatus
  saveSnapshot: () => void
  undo: () => void
  redo: () => void
}

export const useHistoryStore = create<HistoryState>((set, get) => ({
  history: [],
  future: [],
  currentState: TracksStoreInitialState,
  saveStatus: 'persisted',

  saveSnapshot: () => {
    const snapshot = useTracksStore.getState().getSnapshot()

    const { currentState, history } = get()

    if (isDeepEqual(currentState, snapshot)) return

    set({
      history: currentState ? [...history, currentState] : history,
      currentState: snapshot,
      future: [],
      saveStatus: 'dirty',
    })
  },

  undo: () => {
    set((state) => {
      if (state.history.length === 0) return state

      const previousState = state.history[state.history.length - 1] ?? undefined
      const newHistory = state.history.slice(0, -1)

      if (!previousState) return state

      useTracksStore.getState().setSnapshot(previousState)

      return {
        history: newHistory,
        future: state.currentState
          ? [state.currentState, ...state.future]
          : state.future,
        currentState: previousState,
        saveStatus: 'dirty',
      }
    })
  },

  redo: () => {
    set((state) => {
      if (state.future.length === 0) return state

      const nextState = state.future[0] ?? undefined
      const newFuture = state.future.slice(1)

      if (!nextState) return state
      useTracksStore.getState().setSnapshot(nextState)

      return {
        history: state.currentState
          ? [...state.history, state.currentState]
          : state.history,
        future: newFuture,
        currentState: nextState,
        saveStatus: 'dirty',
      }
    })
  },
}))
